import { PAYLOAD_URL } from "../../app/config";
import serialize, { Children } from "../../common/cms/RichText/serialize";
import { WideWidthContainer } from "../../common/components/layout/Container";
import { FullPageLoader } from "../../common/components/loaders/FullPageLoader";
import {
  ActionPreview,
  ActionPreviewProps,
  ActionTextProps,
} from "./ActionPreview";
import { FeedbackCardsPreview } from "./FeedbackCardPreview";
import { PreviewMessage } from "./PreviewMessage";
import {
  QuestionPreview,
  QuestionPreviewProps,
  QuestionTextProps,
} from "./QuestionPreview";
import { useLivePreview } from "@payloadcms/live-preview-react";
import { defaultLanguageCode } from "@pslifestyle/common/src/models/languages";
import { Category } from "@pslifestyle/common/src/types/genericTypes";
import { ActionCategory } from "@pslifestyle/common/src/types/planTypes";
import {
  FeedbackCards as PayloadFeedbackCards,
  Actions as PayloadActions,
  Region as PayloadRegion,
  Questions as PayloadQuestions,
  Category as PayloadCategory,
  Language,
  LocalizedTexts,
  LocalizedRichTexts,
} from "@pslifestyle/payload/src/payload-types";
import { ReactNode, useState } from "react";
import Select from "react-select";

type ArrayElement<ArrayType extends readonly unknown[] | null> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;
type PayloadQuestion = ArrayElement<PayloadQuestions>;
type PayloadAction = ArrayElement<PayloadActions>;

type TextContent = {
  key: string;
  text: string;
  localized?: LocalizedTexts;
};

function getLocalizedText(
  languageCode: string,
  defaultText?: string,
  localizedTexts?: LocalizedTexts,
) {
  return (
    localizedTexts?.find((text) => {
      const payloadLanguage = text.language as Language;
      return payloadLanguage?.code === languageCode;
    })?.text ||
    defaultText ||
    ""
  );
}

function getLocalizedRichText(
  languageCode: string,
  defaultRichText?: { [key: string]: unknown }[] | null,
  localizedRichTexts?: LocalizedRichTexts,
): ReactNode[] {
  const textToRender =
    localizedRichTexts?.find(
      (richText) => (richText.language as Language)?.code === languageCode,
    )?.richText ?? defaultRichText;
  return serialize(textToRender as Children);
}

function getLinks(
  languageCode: string,
  links: {
    link: string;
    text?: string | null;
    localizedLinks?: LocalizedTexts;
    id?: string | null;
  }[],
): { link: string; text: string }[] {
  if (languageCode === defaultLanguageCode) {
    return links
      .filter((link) => link.text)
      .map(({ link, text }) => ({ link, text: text as string }));
  }
  const localizedPreviewLinks: { link: string; text: string }[] = [];
  links.forEach((link) => {
    const localizedLink = link.localizedLinks?.find(
      (link) => (link.language as Language).code === languageCode && link.text,
    );
    if (!localizedLink) {
      return;
    }
    localizedPreviewLinks.push({
      text: localizedLink.text as string,
      link: link.link,
    });
  });
  return localizedPreviewLinks;
}

function getQuestionPreviewProps(
  question: PayloadQuestion,
  languageCode: string,
): QuestionPreviewProps {
  const {
    category: payloadCategory,
    text,
    localizedQuestionTexts,
    description,
    localizedDescriptions,
    answers,
  } = question;
  const category = (payloadCategory as PayloadCategory).name.toLowerCase();

  return {
    category: category as Category,
    text: getLocalizedText(languageCode, text, localizedQuestionTexts),
    description: getLocalizedText(
      languageCode,
      description || undefined,
      localizedDescriptions,
    ),
    choices:
      answers?.map(({ text, localizedAnswerTexts }) =>
        getLocalizedText(languageCode, text, localizedAnswerTexts),
      ) || [],
  };
}
function getQuestionTextProps(
  textContent: {
    common: TextContent[];
  },
  category: PayloadCategory,
  languageCode: string,
): QuestionTextProps {
  const categoryContent = textContent.common.find(
    (content) => content.key === `categories.${category.name.toLowerCase()}`,
  );
  const categoryText = getLocalizedText(
    languageCode,
    categoryContent?.text,
    categoryContent?.localized,
  );
  return { categoryText };
}

function getActionPreviewProps(
  action: PayloadAction,
  languageCode: string,
): ActionPreviewProps {
  const {
    category: payloadCategory,
    title,
    localizedActionTitles,
    description,
    localizedActionDescriptions,
    help,
    localizedActionHelp,
    links,
  } = action;
  const category = (payloadCategory as PayloadCategory).name.toLowerCase();
  return {
    category: category as ActionCategory,
    title: getLocalizedText(languageCode, title, localizedActionTitles),
    description: getLocalizedRichText(
      languageCode,
      description,
      localizedActionDescriptions,
    ),
    help: getLocalizedRichText(languageCode, help, localizedActionHelp),
    links: getLinks(languageCode, links ?? []),
  };
}

function getActionTextProps(
  textContent: {
    common: TextContent[];
    recommendations: TextContent[];
  },
  category: string,
  languageCode: string,
): ActionTextProps {
  const seeMoreContent = textContent.recommendations.find(
    (content) => content.key === "seeMore",
  );
  const helpfulLinksContent = textContent.recommendations.find(
    (content) => content.key === "helpfulLinks",
  );
  const needHelpContent = textContent.recommendations.find(
    (content) => content.key === "needHelpWithThisAction",
  );
  const hideContent = textContent.common.find(
    (content) => content.key === "hide",
  );
  const categoryContent = textContent.common.find(
    (content) => content.key === `categories.${category.toLowerCase()}`,
  );
  const closeContent = textContent.common.find(
    (content) => content.key === "close",
  );
  const seeMoreText = getLocalizedText(
    languageCode,
    seeMoreContent?.text,
    seeMoreContent?.localized,
  );
  const hideText = getLocalizedText(
    languageCode,
    hideContent?.text,
    hideContent?.localized,
  );
  const categoryText = getLocalizedText(
    languageCode,
    categoryContent?.text,
    categoryContent?.localized,
  );
  const helpfulLinksText = getLocalizedText(
    languageCode,
    helpfulLinksContent?.text,
    helpfulLinksContent?.localized,
  );
  const needHelpText = getLocalizedText(
    languageCode,
    needHelpContent?.text,
    needHelpContent?.localized,
  );
  const closeText = getLocalizedText(
    languageCode,
    closeContent?.text,
    closeContent?.localized,
  );
  return {
    seeMoreText,
    hideText,
    categoryText,
    helpfulLinksText,
    needHelpText,
    closeText,
  };
}

function getFeedbackCardsPreviewProps(
  feedbackCards: PayloadFeedbackCards,
  languageCode: string,
) {
  return {
    texts:
      feedbackCards?.map(({ text, localizedTexts }) =>
        getLocalizedText(languageCode, text, localizedTexts),
      ) || [],
  };
}

export const RegionPreviewPage = ({
  region: initialRegion,
}: {
  region?: PayloadRegion | null | undefined;
}) => {
  const { data, isLoading } = useLivePreview({
    initialData: initialRegion,
    serverURL: PAYLOAD_URL,
    depth: 1,
  });

  const [language, setLanguage] = useState<string | undefined>();

  if (isLoading) {
    return <FullPageLoader />;
  }
  if (!data) {
    return <PreviewMessage />;
  }

  const regionLanguages = data.languages as Language[];
  const languageOptions = regionLanguages.map((language) => ({
    value: language.code,
    label: language.code,
  }));
  const defaultOption = {
    value: defaultLanguageCode,
    label: defaultLanguageCode,
  };
  const languageCode = language || defaultOption.value;

  return (
    <div>
      <WideWidthContainer className="px-4 mb-4">
        <p>Select language:</p>
        <Select
          options={[defaultOption, ...languageOptions]}
          defaultValue={defaultOption}
          onChange={(value) => setLanguage(value?.value)}
        />
      </WideWidthContainer>

      {data?.questions?.map((question) => (
        <QuestionPreview
          key={question.id}
          {...getQuestionPreviewProps(question, languageCode)}
          {...getQuestionTextProps(
            { common: data.common ?? [] },
            question.category as PayloadCategory,
            languageCode,
          )}
        />
      ))}

      {data?.actions?.map((action) => (
        <ActionPreview
          key={action.id}
          {...getActionPreviewProps(action, languageCode)}
          {...getActionTextProps(
            {
              common: data.common ?? [],
              recommendations: data.recommendations ?? [],
            },
            (action.category as PayloadCategory).name.toLowerCase(),
            languageCode,
          )}
        />
      ))}

      <FeedbackCardsPreview
        {...getFeedbackCardsPreviewProps(
          data?.feedbackCards || [],
          languageCode,
        )}
      />
    </div>
  );
};
